import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ScrollText, UserCheck, ShieldCheck, Gavel, CreditCard,
  AlertTriangle, Ban, Handshake, Scale, Globe2,
  Mail, Calendar, ArrowUp, ChevronRight, FileSignature,
} from 'lucide-react';

const SUPPORT_EMAIL = 'membership@dominicanchamberfl.org';
const EFFECTIVE_DATE = 'February 1, 2026';

const sections = [
  { id: 'acceptance', label: '1. Acceptance of terms', icon: FileSignature },
  { id: 'who-can-join', label: '2. Who can join', icon: UserCheck },
  { id: 'accounts', label: '3. Your account', icon: ShieldCheck },
  { id: 'membership-payments', label: '4. Membership & payments', icon: CreditCard },
  { id: 'conduct', label: '5. Member conduct', icon: Handshake },
  { id: 'content', label: '6. Content you publish', icon: ScrollText },
  { id: 'prohibited', label: '7. Prohibited activity', icon: Ban },
  { id: 'third-parties', label: '8. Third-party links', icon: Globe2 },
  { id: 'termination', label: '9. Suspension & termination', icon: AlertTriangle },
  { id: 'disclaimers', label: '10. Disclaimers', icon: Scale },
  { id: 'liability', label: '11. Limitation of liability', icon: Gavel },
  { id: 'governing-law', label: '12. Governing law', icon: Scale },
  { id: 'changes', label: '13. Changes to these terms', icon: Calendar },
  { id: 'contact', label: '14. Contact us', icon: Mail },
];

const TermsOfServicePage = () => {
  useEffect(() => {
    document.title = 'Terms of Service · DCCF Network';
    return () => { document.title = 'DCCF | Dominican Chamber of Commerce of Florida'; };
  }, []);

  const scrollTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-slate-50 pt-14 sm:pt-20" data-testid="terms-of-service-page">
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#002249] text-white">
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage:
              'radial-gradient(circle at 10% 20%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #E63946 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#002249] via-[#001530] to-[#0a3570]" style={{ mixBlendMode: 'multiply' }} />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 py-12 sm:py-16">
          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="text-[10.5px] sm:text-xs font-bold text-amber-300 tracking-[0.18em] uppercase mb-3 block">
              Legal · Terms
            </span>
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-3">
              Terms of Service
            </h1>
            <p className="text-white/75 text-base sm:text-lg max-w-2xl leading-relaxed">
              The agreement between the Dominican Chamber of Commerce of Florida and everyone who uses our website, member portal, and mobile apps.
            </p>
            <p className="text-white/55 text-[13px] mt-4">Effective: {EFFECTIVE_DATE}</p>
          </motion.div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10 sm:py-14">
        <div className="grid lg:grid-cols-[260px_1fr] gap-8 lg:gap-12">
          {/* Sticky TOC */}
          <aside className="lg:sticky lg:top-24 lg:self-start order-2 lg:order-1" aria-label="Table of contents">
            <nav className="bg-white border border-slate-200 rounded-2xl p-4 lg:p-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#E63946] mb-3">On this page</p>
              <ul className="space-y-1">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className="group flex items-center gap-2 px-2 py-1.5 rounded-md text-[12.5px] text-slate-600 hover:bg-slate-50 hover:text-[#002249] transition-colors"
                      data-testid={`terms-toc-${s.id}`}
                    >
                      <s.icon className="w-3.5 h-3.5 text-slate-400 group-hover:text-[#E63946] flex-shrink-0" />
                      <span className="truncate">{s.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Body */}
          <article className="order-1 lg:order-2 max-w-[680px]" data-testid="terms-content">
            {/* Intro */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-8 shadow-[0_1px_3px_rgba(15,23,42,0.04)]">
              <p className="text-[14.5px] sm:text-[15px] text-slate-700 leading-relaxed">
                Welcome to <strong className="text-[#002249]">DCCF Network</strong> — the digital home of the
                Dominican Chamber of Commerce of Florida. These Terms of Service ("Terms") are the legal
                agreement between you and the <strong className="text-[#002249]">Dominican Chamber of Commerce of Florida</strong>
                {' '}("DCCF", "we", "our") that governs your use of our website
                (<a href="https://dominicanchamberfl.org" className="text-[#002249] font-semibold hover:underline">dominicanchamberfl.org</a>),
                our member portal at <span className="font-mono text-[13px] bg-slate-100 px-1.5 py-0.5 rounded">/app</span>,
                and the DCCF Network mobile apps for iOS and Android (together, the "Platform").
              </p>
              <p className="text-[14px] text-slate-600 leading-relaxed mt-3">
                By creating an account, joining as a member, or simply browsing the Platform, you confirm that
                you have read, understood, and agree to be bound by these Terms and by our{' '}
                <Link to="/privacy" className="text-[#002249] font-semibold hover:underline">Privacy Policy</Link>.
                If you do not agree, please discontinue use.
              </p>
            </div>

            <Section id="acceptance" title="1. Acceptance of terms" icon={FileSignature}>
              <p>
                These Terms form a binding contract between you and DCCF. Certain areas of the Platform — such as
                the member portal, member directory, events registration, direct messages, and the mobile apps — may
                require an active membership and additional acceptance of membership policies specific to that tier.
              </p>
              <p>
                If you accept these Terms on behalf of a company or organization, you represent that you have the
                authority to bind that entity, and "you" refers to that entity as well as yourself.
              </p>
            </Section>

            <Section id="who-can-join" title="2. Who can join" icon={UserCheck}>
              <p>To use DCCF Network you must:</p>
              <BulletList items={[
                'Be at least 18 years old',
                'Provide accurate, current, and complete information when registering',
                'Not be located in a jurisdiction where DCCF services are prohibited',
                'Not have been previously suspended or removed from DCCF for a material violation of these Terms',
              ]} />
              <p>
                DCCF membership is open to individuals and businesses that support our mission. Applications are
                reviewed by Membership Relations and acceptance is at the sole discretion of the Chamber.
              </p>
            </Section>

            <Section id="accounts" title="3. Your account" icon={ShieldCheck}>
              <p>You are responsible for:</p>
              <BulletList items={[
                'Keeping your login credentials confidential — do not share your password',
                'All activity that occurs under your account, whether or not authorized by you',
                'Notifying us immediately at <a href="mailto:' + SUPPORT_EMAIL + '" class="text-[#002249] font-semibold hover:underline">' + SUPPORT_EMAIL + '</a> if you suspect unauthorized access',
                'Keeping your profile information accurate and up to date',
              ]} />
              <p>
                DCCF may suspend or terminate accounts that show signs of compromise, impersonation, bot activity,
                or material misrepresentation, with or without prior notice.
              </p>
            </Section>

            <Section id="membership-payments" title="4. Membership &amp; payments" icon={CreditCard}>
              <SubHeading>Tiers</SubHeading>
              <p>
                DCCF offers multiple membership tiers — Hybrid, Essential, Premier, and Executive — each with its
                own benefits package, reach, and investment level. Current pricing is displayed at{' '}
                <Link to="/membership" className="text-[#002249] font-semibold hover:underline">/membership</Link>.
              </p>

              <SubHeading>Billing</SubHeading>
              <BulletList items={[
                'Membership fees are billed annually unless explicitly stated otherwise',
                'Payments may be processed by DCCF directly or through third-party platforms (Zeffy, Square, PayPal, etc.)',
                'You are responsible for all applicable taxes on your membership',
                'Failed payments may result in suspension of member-only features until resolved',
              ]} />

              <SubHeading>Renewals &amp; cancellations</SubHeading>
              <BulletList items={[
                'Memberships renew annually on the anniversary of your join date',
                'You may cancel at any time by emailing ' + SUPPORT_EMAIL,
                'Cancellations take effect at the end of the current billing cycle — we do not issue partial-year refunds except as noted below',
              ]} />

              <SubHeading>Refunds</SubHeading>
              <p>
                Membership fees are generally non-refundable. As a courtesy, new members may request a full refund
                within <strong>14 calendar days</strong> of initial payment, provided they have not yet attended a
                paid event, redeemed a member benefit, or accepted a directory listing. Refund requests are reviewed
                case-by-case by Membership Relations.
              </p>

              <SubHeading>Event tickets &amp; sponsorships</SubHeading>
              <p>
                Event tickets and sponsorships are governed by the terms shown at the point of purchase. Unless
                otherwise stated, event purchases are non-refundable but may be transferred to another member
                up to 48 hours before the event.
              </p>
            </Section>

            <Section id="conduct" title="5. Member conduct" icon={Handshake}>
              <p>
                DCCF Network is a professional community. By using the Platform you agree to interact with other
                members, staff, and leadership in a manner that is:
              </p>
              <BulletList items={[
                'Respectful — no harassment, hate speech, threats, or discriminatory conduct',
                'Honest — no impersonation, misrepresentation of credentials, or deceptive business claims',
                'Professional — keep communications business-appropriate',
                'Constructive — disagreements are welcome; personal attacks are not',
                'Compliant with applicable law — you are responsible for your own compliance',
              ]} />
            </Section>

            <Section id="content" title="6. Content you publish" icon={ScrollText}>
              <SubHeading>Ownership</SubHeading>
              <p>
                You retain ownership of the content you publish on the Platform — including posts, comments,
                photos, logos, event recaps, blog submissions, and member-benefit offers ("Your Content").
              </p>

              <SubHeading>License to DCCF</SubHeading>
              <p>
                By publishing Your Content you grant DCCF a worldwide, royalty-free, non-exclusive license to host,
                store, display, reproduce, distribute, and promote Your Content <strong>within the Platform and in
                DCCF marketing materials that showcase the Chamber's activities</strong> (e.g., newsletters,
                recap blog posts, social-media highlights). This license lasts as long as Your Content remains on
                the Platform and ends when you delete it.
              </p>

              <SubHeading>Your responsibilities</SubHeading>
              <BulletList items={[
                'You own the rights, or have permission, for every element of Your Content (including images, logos, music, and quotes)',
                'Your Content does not infringe any third-party intellectual-property, privacy, or publicity right',
                'Your Content is accurate and not misleading',
                'Your Content complies with applicable advertising, healthcare, financial, and consumer-protection laws',
              ]} />

              <SubHeading>Moderation</SubHeading>
              <p>
                DCCF reserves the right — but is not obligated — to review, edit, or remove any content that
                violates these Terms or that we reasonably believe harms members, the Chamber, or the public.
              </p>
            </Section>

            <Section id="prohibited" title="7. Prohibited activity" icon={Ban}>
              <p>You agree <strong>not</strong> to:</p>
              <BulletList items={[
                'Post spam, pyramid schemes, multi-level-marketing recruitment, or unsolicited sales messages',
                'Send bulk direct messages or automated friend requests',
                'Scrape, crawl, or index the Platform without written permission',
                'Reverse-engineer, decompile, or attempt to extract source code',
                'Upload viruses, malware, or code designed to disrupt the Platform',
                'Attempt to bypass security, rate limits, or access-control mechanisms',
                'Use the Platform to harass, defame, dox, or threaten any person',
                'Publish sexually explicit, violent, or illegal content',
                'Misrepresent your affiliation with DCCF, any leader, committee, or sponsor',
                'Use the DCCF name, logo, or brand without prior written consent outside the Platform',
                'Resell, sublicense, or transfer your account or membership to anyone else',
              ]} />
              <p>
                Violations may result in content removal, account suspension, permanent removal from the Platform,
                and — when applicable — referral to law enforcement.
              </p>
            </Section>

            <Section id="third-parties" title="8. Third-party links &amp; services" icon={Globe2}>
              <p>
                The Platform may link to or integrate third-party websites, payment processors, email delivery
                services, push-notification providers, and AI-assisted tools. DCCF does not control these
                third parties and is not responsible for their content, practices, terms, or privacy policies.
              </p>
              <p>
                When you engage with a third-party service from the Platform, you do so under that service's own
                terms. We recommend reviewing them before proceeding.
              </p>
            </Section>

            <Section id="termination" title="9. Suspension &amp; termination" icon={AlertTriangle}>
              <p>DCCF may suspend or terminate your access to all or part of the Platform if:</p>
              <BulletList items={[
                'You violate these Terms or any policy incorporated by reference',
                'Your membership fees are overdue',
                'Your conduct harms another member, the Chamber, or the integrity of the Platform',
                'We are required to do so by law or by a regulator',
                'You request deletion of your account',
              ]} />
              <p>
                You may close your account at any time by emailing{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-[#002249] font-semibold hover:underline">{SUPPORT_EMAIL}</a>.
                Upon termination, sections 6 (content license, limited to content already used in DCCF materials),
                10, 11, 12, and 14 will survive.
              </p>
            </Section>

            <Section id="disclaimers" title="10. Disclaimers" icon={Scale}>
              <p>
                The Platform is provided <strong>"as is" and "as available"</strong>. To the fullest extent
                permitted by law, DCCF disclaims all warranties — express, implied, statutory, or otherwise —
                including warranties of merchantability, fitness for a particular purpose, title, non-infringement,
                and uninterrupted or error-free operation.
              </p>
              <p>
                DCCF does not guarantee business outcomes, returns on membership investment, referral volume, or
                revenue growth. Content shared by other members — including member-benefit offers, business advice,
                and professional opinions — is their own and does not constitute endorsement by the Chamber.
              </p>
            </Section>

            <Section id="liability" title="11. Limitation of liability" icon={Gavel}>
              <p>
                To the maximum extent permitted by law, DCCF, its officers, directors, employees, leadership
                council members, volunteers, and contractors will <strong>not</strong> be liable for any indirect,
                incidental, consequential, special, exemplary, or punitive damages — including lost profits, lost
                business opportunity, lost data, or reputational harm — arising out of or related to your use of
                the Platform, even if we have been advised of the possibility of such damages.
              </p>
              <p>
                Our total aggregate liability for any claim relating to these Terms or the Platform will not
                exceed the greater of: <strong>(a)</strong> the amount you paid to DCCF in the twelve (12) months
                immediately preceding the event giving rise to the claim, or <strong>(b)</strong> one hundred US
                dollars (USD $100).
              </p>
            </Section>

            <Section id="governing-law" title="12. Governing law &amp; disputes" icon={Scale}>
              <p>
                These Terms are governed by the laws of the <strong>State of Florida, USA</strong>, without regard
                to its conflict-of-laws principles.
              </p>
              <p>
                Any dispute arising out of or related to these Terms or the Platform will first be addressed
                through good-faith negotiation with Membership Relations. If unresolved after 30 days, disputes
                will be submitted to the exclusive jurisdiction of the state and federal courts located in{' '}
                <strong>Osceola County, Florida</strong>, and both parties consent to personal jurisdiction there.
              </p>
              <p>
                Nothing in this section prevents either party from seeking injunctive or equitable relief to
                protect intellectual-property rights in any court of competent jurisdiction.
              </p>
            </Section>

            <Section id="changes" title="13. Changes to these terms" icon={Calendar}>
              <p>
                We may update these Terms as the Platform evolves, as laws change, or as our membership
                offerings grow. When we make material changes we will:
              </p>
              <BulletList items={[
                'Post the updated Terms at /terms with a new "Effective" date',
                'Notify members by email at least 14 days before changes take effect',
                'Highlight the changes inside the Platform on next sign-in',
              ]} />
              <p>
                Continued use of the Platform after the effective date constitutes acceptance of the updated Terms.
              </p>
            </Section>

            <Section id="contact" title="14. Contact us" icon={Mail}>
              <p>
                Questions about these Terms, your membership, or a specific clause?
              </p>
              <div className="bg-gradient-to-br from-[#002249] to-[#001530] rounded-2xl p-6 sm:p-7 text-white mt-3 not-prose">
                <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-amber-300 mb-2">Legal contact</p>
                <p className="text-[16px] font-bold tracking-tight">Membership Relations</p>
                <p className="text-[13px] text-white/70 mt-1.5 leading-relaxed">
                  Dominican Chamber of Commerce of Florida<br />
                  28 Broadway Ave, Suite 209 · Kissimmee, FL 34741
                </p>
                <a
                  href={`mailto:${SUPPORT_EMAIL}`}
                  className="mt-4 inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#E63946] hover:bg-[#c02a35] text-white text-[13px] font-bold transition-colors"
                  data-testid="terms-contact-cta"
                >
                  <Mail className="w-4 h-4" />
                  {SUPPORT_EMAIL}
                </a>
                <p className="text-[12px] text-white/60 mt-3">We respond to Terms-related questions within 30 days.</p>
              </div>
            </Section>

            {/* Footer nav */}
            <div className="mt-10 pt-6 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/" className="text-[13px] font-semibold text-[#002249] hover:underline inline-flex items-center gap-1" data-testid="terms-back-home">
                  <ChevronRight className="w-3.5 h-3.5 rotate-180" /> Back to DCCF
                </Link>
                <Link to="/privacy" className="text-[13px] font-medium text-slate-500 hover:text-[#002249] hover:underline" data-testid="terms-privacy-crosslink">
                  Privacy Policy
                </Link>
              </div>
              <button
                onClick={scrollTop}
                className="text-[12.5px] font-semibold text-slate-500 hover:text-[#002249] inline-flex items-center gap-1"
                data-testid="terms-scroll-top"
              >
                <ArrowUp className="w-3.5 h-3.5" /> Back to top
              </button>
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

/* ─── Sub-components ──────────────────────────────────────────────────────── */

const Section = ({ id, title, icon: Icon, children }) => (
  <motion.section
    id={id}
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.08 }}
    transition={{ duration: 0.45 }}
    className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 mb-5 shadow-[0_1px_3px_rgba(15,23,42,0.04)] scroll-mt-24"
    data-testid={`terms-section-${id}`}
  >
    <div className="flex items-center gap-3 mb-4">
      <span className="w-9 h-9 rounded-xl bg-[#002249]/5 text-[#002249] flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4" />
      </span>
      <h2 className="text-[19px] sm:text-[22px] font-bold text-[#002249] tracking-tight leading-tight">{title}</h2>
    </div>
    <div className="prose-content space-y-3 text-[14px] sm:text-[14.5px] text-slate-700 leading-relaxed">
      {children}
    </div>
  </motion.section>
);

const SubHeading = ({ children }) => (
  <p className="text-[10.5px] font-bold uppercase tracking-[0.16em] text-[#E63946] mt-5 mb-2">
    {children}
  </p>
);

const BulletList = ({ items }) => (
  <ul className="space-y-1.5 my-2 ml-0.5">
    {items.map((item, i) => (
      <li key={i} className="flex items-start gap-2.5 text-[14px] text-slate-600 leading-relaxed">
        <span className="w-1.5 h-1.5 rounded-full bg-[#E63946] mt-2 flex-shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: item }} />
      </li>
    ))}
  </ul>
);

export default TermsOfServicePage;
